/**
 * English - the reference dictionary.
 *
 * Every other locale is typed as `Dictionary`, so a missing or misspelled key
 * is a compile error rather than a string that silently renders as its own key
 * in production.
 *
 * Keys are flat and dot-separated: easy to diff across six files, and no type
 * gymnastics to read a nested path.
 *
 * Placeholders use `{name}` and are substituted by `t()`.
 */
export const en = {
  /* ---- Common ---------------------------------------------------------- */
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.retry': 'Try again',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.share': 'Share',
  'common.delete': 'Delete',
  'common.revoke': 'Revoke',
  'common.close': 'Close',
  'common.loading': 'Loading',
  'common.optional': 'Optional',
  'common.or': 'or',
  'common.none': 'None',
  'common.required': 'Required',
  'common.notSet': 'Not set',
  'common.skipToContent': 'Skip to main content',
  'common.moment': 'One moment…',

  /* ---- Brand ----------------------------------------------------------- */
  'brand.tagline': 'Share a video. Once.',
  'brand.home': 'BOOMSHOT - home',
  'brand.footerLimit':
    'BOOMSHOT provides secure single playback. No technology can prevent a screenshot or a screen recording.',

  /* ---- Navigation ------------------------------------------------------ */
  'nav.main': 'Main navigation',
  'nav.myVideos': 'My videos',
  'nav.privacy': 'Privacy',
  'nav.signIn': 'Sign in',
  'nav.signOut': 'Sign out',
  'nav.settings': 'Account settings',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',

  /* ---- Theme ----------------------------------------------------------- */
  'theme.light': 'Light theme - switch to dark',
  'theme.dark': 'Dark theme - switch to automatic',
  'theme.system': 'Automatic theme - switch to light',
  'theme.label': 'Appearance',
  'theme.optionSystem': 'Automatic',
  'theme.optionLight': 'Light',
  'theme.optionDark': 'Dark',

  /* ---- Language -------------------------------------------------------- */
  'language.label': 'Language',
  'language.change': 'Change language',

  /* ---- Landing --------------------------------------------------------- */
  'landing.title': 'Share a video. Once.',
  'landing.subtitle':
    'Your private link stops working after the first viewing, and the video is deleted automatically.',
  'landing.ctaNew': 'Create a private link',
  'landing.ctaSend': 'Send a video',
  'landing.haveAccount': 'I already have an account',
  'landing.assure.encrypted': 'Encrypted in transit',
  'landing.assure.deleted': 'Deleted after viewing',
  'landing.assure.noReplay': 'No replay',
  'landing.how': 'Three steps. No second chance.',
  'landing.step1.title': 'Send',
  'landing.step1.body':
    'Drop in a video. It is converted into fragments served from private storage, never from a public URL.',
  'landing.step2.title': 'Configure',
  'landing.step2.body':
    'Expiry, access code, watermark, message. The link carries a secret that never leaves your device.',
  'landing.step3.title': 'One viewing',
  'landing.step3.body':
    'The first session reserves the link permanently. When it ends, the files are destroyed server-side.',
  'landing.limits.title': 'What BOOMSHOT does',
  'landing.limits.guaranteed': 'Guaranteed server-side',
  'landing.limits.g1': 'One playback session per link, reserved atomically.',
  'landing.limits.g2': 'Fragments served under short-lived tokens, with no permanent URL.',
  'landing.limits.g3': 'Verified deletion of the source file, fragments and thumbnails.',

  /* ---- Auth ------------------------------------------------------------ */
  'auth.signIn.title': 'Sign in',
  'auth.signIn.subtitle': 'Access your videos and your active links.',
  'auth.signIn.submit': 'Sign in',
  'auth.signIn.noAccount': 'No account yet?',
  'auth.signIn.create': 'Create an account',
  'auth.signIn.magicLink': 'Send me a sign-in link',
  'auth.signIn.usePassword': 'Use a password instead',
  'auth.signIn.forgot': 'Forgot password',
  'auth.signIn.magicSent':
    'If an account exists for this address, a sign-in link has just been sent. It works only once.',
  'auth.signUp.title': 'Create an account',
  'auth.signUp.subtitle':
    'An account is needed to send. Your recipients never need one.',
  'auth.signUp.submit': 'Create my account',
  'auth.signUp.hasAccount': 'Already registered?',
  'auth.signUp.terms':
    'I understand that a video played in a browser can always be captured by a screen recording, and that BOOMSHOT guarantees single playback, not copy protection.',
  'auth.reset.title': 'Forgot password',
  'auth.reset.subtitle': 'We will send you a reset link.',
  'auth.reset.submit': 'Send the link',
  'auth.reset.sent':
    'If an account exists for this address, a reset email has just been sent. Remember to check your spam folder.',
  'auth.reset.back': 'Back to sign in',
  'auth.callback.title': 'Signing you in',
  'auth.callback.subtitle': 'Checking your link…',
  'auth.callback.invalid': 'This is not a valid sign-in link.',
  'auth.callback.requestNew': 'Request a new link',
  'auth.google': 'Continue with Google',
  'auth.email': 'Email address',
  'auth.emailPlaceholder': 'you@example.com',
  'auth.password': 'Password',
  'auth.passwordConfirm': 'Confirm password',
  'auth.passwordHint': 'At least 10 characters, with an uppercase, a lowercase and a digit.',
  'auth.magicHint': 'You will receive a single-use link, no password needed.',
  'auth.confirmEmailPrompt': 'Confirm your email address to finish signing in',

  /* ---- Auth errors ----------------------------------------------------- */
  'authError.invalidCredential': 'Incorrect email address or password.',
  'authError.emailInUse':
    'That account could not be created. Try signing in or resetting your password.',
  'authError.weakPassword': 'Password too weak.',
  'authError.tooManyRequests': 'Too many attempts. Try again in a few minutes.',
  'authError.network': 'No connection. Check your network.',
  'authError.popupClosed': 'The window was closed before sign-in finished.',
  'authError.popupBlocked': 'The window was blocked by your browser.',
  'authError.recentLogin': 'For security, please sign in again before continuing.',
  'authError.invalidActionCode': 'This sign-in link is invalid or has already been used.',
  'authError.expiredActionCode': 'This sign-in link has expired. Request a new one.',
  'authError.generic': 'Something went wrong. Please try again.',

  /* ---- Dashboard ------------------------------------------------------- */
  'dashboard.title': 'My videos',
  'dashboard.subtitle': 'Each link allows exactly one playback session.',
  'dashboard.send': 'Send a video',
  'dashboard.metric.activeLinks': 'Active links',
  'dashboard.metric.watched': 'Videos viewed',
  'dashboard.metric.storage': 'Temporary storage',
  'dashboard.search': 'Search',
  'dashboard.searchPlaceholder': 'Search by name or recipient',
  'dashboard.filters': 'Filters',
  'dashboard.filterStatus': 'Status',
  'dashboard.filterAll': 'All statuses',
  'dashboard.sort': 'Sort',
  'dashboard.sortNewest': 'Newest first',
  'dashboard.sortOldest': 'Oldest first',
  'dashboard.sortExpiring': 'Expiring soonest',
  'dashboard.empty.title': 'No videos yet',
  'dashboard.empty.body':
    'Send your first video: it will be converted, protected by a single-use link, then deleted after the first viewing.',
  'dashboard.noResults.title': 'No results',
  'dashboard.noResults.body':
    'No video matches this search. Try another term or clear the filters.',
  'dashboard.loadError': 'Could not load your videos. Check your connection.',
  'dashboard.loadBlocked': 'Could not load your videos. The service is not configured correctly - this is not a problem on your side.',
  'dashboard.expiresIn': 'Expires {when}',
  'dashboard.viewedAt': 'Viewed {when}',
  'dashboard.createdAt': 'Created {when}',
  'dashboard.forRecipient': 'For {name}',

  /* ---- Status ---------------------------------------------------------- */
  'status.processing': 'Processing',
  'status.ready': 'Ready',
  'status.opened': 'Link opened',
  'status.playing': 'Playing',
  'status.watched': 'Viewed',
  'status.expired': 'Expired',
  'status.deleted': 'Deleted',
  'status.error': 'Error',

  /* ---- Upload ---------------------------------------------------------- */
  'upload.title': 'Send a video',
  'upload.subtitle':
    'It will be converted, protected by a single-use link, then deleted after the first viewing.',
  'upload.drop': 'Drop your video here',
  'upload.formats': '{formats} - up to {size}',
  'upload.choose': 'Choose a file',
  'upload.progress': 'Upload progress',
  'upload.inProgress': 'Uploading…',
  'upload.converting': 'Converting…',
  'upload.cancelUpload': 'Cancel upload',
  'upload.processingNote':
    "Converting to secure fragments. You don't need to wait - generate the link now and it'll work as soon as this finishes.",
  'upload.restart': 'Start over',
  'upload.error.format': 'Unsupported format. Use an MP4, MOV or WebM file.',
  'upload.error.tooLarge': 'File too large (maximum {size}).',
  'upload.error.notVideo': 'This file does not look like a valid video.',
  'upload.error.unreadable': 'This file could not be read as a video.',
  'upload.error.tooLong': 'Video too long ({duration}). Maximum {max}.',
  'upload.error.failed': 'The upload failed. Check your connection and try again.',
  'upload.error.transcode':
    'Converting this video failed. The file has been deleted. Try another format.',
  'upload.error.tracking': 'Lost track of processing. Reload the page.',

  /* ---- Link configuration ---------------------------------------------- */
  'config.expiry': 'Link expiry',
  'config.expiry1h': '1 hour',
  'config.expiry24h': '24 h',
  'config.expiry3d': '3 days',
  'config.expiry7d': '7 days',
  'config.expiryCustom': 'Custom date',
  'config.expiryDate': 'Expiry date and time',
  'config.pin': 'Access code',
  'config.pinHint': '4 to 8 digits. Send it separately from the link.',
  'config.advanced': 'Advanced options',
  'config.internalName': 'Internal name',
  'config.internalNameHint': 'Visible only to you, never to the recipient.',
  'config.recipient': 'Recipient name',
  'config.recipientHint': 'Shown in the watermark when it is enabled.',
  'config.watermark': 'Watermark',
  'config.watermarkNone': 'None',
  'config.watermarkSubtle': 'Subtle',
  'config.watermarkReinforced': 'Reinforced',
  'config.message': 'Message to the recipient',
  'config.messagePlaceholder': 'Optional - shown before playback',
  'config.submit': 'Create the private link',
  'config.alwaysDeleted': 'Deletion after viewing is always on and cannot be turned off.',
  'config.linkFailed': 'Could not generate the link.',

  /* ---- Link ready ------------------------------------------------------ */
  'ready.eyebrow': 'Link generated',
  'ready.title': 'Your link is ready. It works once.',
  'ready.meta': '{name} · {duration} · expires {date}',
  'ready.stillProcessing':
    "This link already works, but the video is still processing. If it's opened right away, the recipient will just be asked to wait a moment.",
  'ready.secureLink': 'Secure link',
  'ready.copyFailed': 'Could not copy. Select the link manually.',
  'ready.shareTitle': 'Single-view video',
  'ready.shareText': 'This video can only be watched once.',
  'ready.pinWarning':
    'The access code is deliberately excluded from the share. Send it through another channel (text, a call), never in the same message as the link.',
  'ready.fragmentNote':
    'The part of the link after the {hash} is the access secret. It is never sent to our servers - copy the link in full.',
  'ready.protections': 'Active protections',
  'ready.protectionSingle': 'One playback session',
  'ready.protectionDelete': 'Deleted after viewing',
  'ready.protectionPin': 'Access code required',
  'ready.protectionsNote':
    'These measures guarantee single playback. They cannot prevent a screenshot or a screen recording.',
  'ready.qr': 'QR code',
  'ready.qrAlt': 'QR code for the secure link to {name}',
  'ready.sendAnother': 'Send another video',
  'ready.backToDashboard': 'Back to dashboard',
  'ready.leaveWarnTitle': "You haven't copied this link yet",
  'ready.leaveWarnBody':
    "This is the only time it's shown. It isn't stored anywhere, so if you leave now without copying or sharing it, no one - including you - will be able to retrieve it later. You'd need to revoke this video's link and send it again.",
  'ready.leaveAnyway': 'Leave without copying',
  'ready.copyNow': 'Copy the link',
  'ready.watermarkNone': 'No watermark',
  'ready.watermarkSubtle': 'Subtle watermark',
  'ready.watermarkReinforced': 'Reinforced watermark',

  /* ---- Video detail ---------------------------------------------------- */
  'detail.notFound.title': 'Video not found',
  'detail.notFound.body': 'This video no longer exists, or it is not yours.',
  'detail.duration': 'Duration',
  'detail.size': 'Size',
  'detail.created': 'Created',
  'detail.recipient': 'Recipient',
  'detail.expiry': 'Expiry',
  'detail.viewedOn': 'Viewed on',
  'detail.deletedOn': 'Deleted on',
  'detail.pinRequired': 'Required',
  'detail.consumedNote':
    'This video has been viewed. The link is permanently unusable and the files have been deleted from storage.',
  'detail.failedNote':
    'Processing this video failed. No link was generated and the temporary files have been deleted.',
  'detail.lastError': 'Last error reported by the server:',
  'detail.copyLink': 'Copy link',
  'detail.secretLost':
    'The link secret is never stored: it appears only at generation time. Revoke this link and create a new one if you have lost it.',
  'detail.revoke': 'Revoke link',
  'detail.deleteNow': 'Delete now',
  'detail.reuse': 'Reuse these settings',
  'detail.revokeTitle': 'Revoke this link?',
  'detail.revokeBody':
    'The link will stop working immediately. The video will then be deleted.',
  'detail.revoked': 'Link revoked.',
  'detail.deleteTitle': 'Delete this video?',
  'detail.deleteBody':
    'The source file, fragments and thumbnails will be destroyed. This cannot be undone.',
  'detail.deleteStarted': 'Deletion started.',
  'detail.actionFailed': 'That action is not possible right now.',

  /* ---- Settings -------------------------------------------------------- */
  'settings.title': 'Account and privacy',
  'settings.subtitle': 'What BOOMSHOT keeps, and what it cannot guarantee.',
  'settings.identity': 'Identity',
  'settings.anonymous': 'Anonymous account',
  'settings.memberSince': 'Member since {date}',
  'settings.dataKept': 'Data retained',
  'settings.dataKept1':
    'After a video is deleted, BOOMSHOT keeps only a minimal trace: status, creation date, viewing date, deletion date, duration and the name you chose.',
  'settings.dataKept2':
    'No full IP address is stored. No thumbnail, video fragment or source file remains.',
  'settings.limitsTitle': 'Real limits of the protection',
  'settings.limits1':
    'A video played in a browser can always be copied: screenshot, screen recording, an external camera filming the screen, or extraction by a technically capable viewer.',
  'settings.limits2':
    'BOOMSHOT guarantees that a link opens exactly one playback session and that the files are then destroyed server-side. It does not guarantee that the image cannot be copied.',
  'settings.deleteAccount': 'Delete account',
  'settings.deleteAccountBody':
    'All your videos, links and files will be destroyed. This cannot be undone.',
  'settings.deleteConfirmLabel': 'Type DELETE to confirm',
  'settings.deleteConfirmWord': 'DELETE',
  'settings.deleteSubmit': 'Permanently delete my account',
  'settings.deleted': 'Account deleted.',
  'settings.signOutFailed': 'Could not sign out right now.',

  /* ---- Viewer ---------------------------------------------------------- */
  'viewer.checking': 'Checking the link…',
  'viewer.preparing': "Almost there - the sender's video is still being prepared.",
  'viewer.eyebrow': 'Single playback',
  'viewer.title': 'This video can only be watched once.',
  'viewer.subtitle':
    'Start it when you are ready, and make sure you have a stable connection.',
  'viewer.sender': 'Sender',
  'viewer.message': 'Message',
  'viewer.duration': 'Duration',
  'viewer.deadline': 'Available until',
  'viewer.warning':
    'As soon as you start playback, the link becomes permanently unusable. There is no replay, no rewind and no second session - even if you close the page by accident.',
  'viewer.start': 'Start the single viewing',
  'viewer.startWithPin': 'Confirm and start the single viewing',
  'viewer.pinHint': 'The sender gave you this code separately.',
  'viewer.pinWrong': 'Incorrect code.',
  'viewer.pinWrongRetry': 'Incorrect code. Try again in {seconds}s.',
  'viewer.retryIn': 'Try again in {seconds}s',
  'viewer.noPreload': 'No video fragment is loaded before you confirm.',
  'viewer.checkFailed': 'Could not check this link right now.',
  'viewer.claimFailed': 'The playback session could not be opened.',
  'viewer.unavailable.title': 'This link is no longer valid',
  'viewer.unavailable.body':
    'It may already have been used or revoked, or it may never have existed. For privacy reasons we cannot say more. Ask the sender for a new link.',
  'viewer.expired.title': 'This link has expired',
  'viewer.expired.body':
    'The deadline set by the sender has passed and the video has been deleted. Ask for a new link if you still need to watch it.',
  'viewer.used.title': 'This video has already been opened',
  'viewer.used.body':
    'A playback session has already started for this link. It cannot create another, and the video has been deleted.',
  'viewer.stillProcessing.title': 'Still getting this video ready',
  'viewer.stillProcessing.body':
    "This is taking longer than usual. The link itself is fine - reopen it in a few minutes and it should be ready to watch.",
  'viewer.consumed.title': 'Video viewed and deleted',
  'viewer.consumed.body':
    'Playback is complete. The link is now unusable and the files have been destroyed on our servers.',
  'viewer.error.title': 'Playback impossible',

  /* ---- Player ---------------------------------------------------------- */
  'player.preloading': 'Secure preloading…',
  'player.tapToPlay': 'Tap to play',
  'player.tapToPlayHint': 'The single viewing starts the moment you press play.',
  'player.stalled': 'Unstable connection - playback will resume automatically.',
  'player.play': 'Play',
  'player.pause': 'Pause',
  'player.mute': 'Mute',
  'player.unmute': 'Unmute',
  'player.volume': 'Volume',
  'player.fullscreen': 'Enter fullscreen',
  'player.exitFullscreen': 'Exit fullscreen',
  'player.progress': 'Playback progress',
  'player.unsupported': 'Your browser cannot play this video.',
  'player.fatal': 'Playback was interrupted and cannot resume.',

  /* ---- Not found ------------------------------------------------------- */
  'notFound.title': 'Page not found',
  'notFound.body':
    'This address does not match anything. If you were sent a video link, check that it was copied in full - the part after the {hash} is essential.',
  'notFound.home': 'Back to home',

  /* ---- API errors ------------------------------------------------------ */
  'apiError.network': 'No connection. Check your network.',
  'apiError.unauthenticated': 'Session expired. Please sign in again.',
  'apiError.forbidden': 'Access denied.',
  'apiError.notFound': 'Not found.',
  'apiError.conflict': 'That action has already been performed.',
  'apiError.rateLimited': 'Too many attempts. Try again shortly.',
  'apiError.badResponse': 'Unexpected response from the server.',
  'apiError.internal': 'Something went wrong on our side.',
  'apiError.badRequest': 'Invalid request.',

  /* ---- Validation (mirrors the keys emitted by @once/shared schemas) ---- */
  'validation.required': 'Required',
  'validation.invalidFormat': 'Invalid format',
  'validation.tooLong': 'Too long',
  'validation.unsafeCharacters': 'Characters not allowed',
  'validation.email': 'Invalid email address',
  'validation.passwordMin': 'At least 10 characters',
  'validation.passwordComplexity': 'Must contain a lowercase, an uppercase and a digit',
  'validation.passwordMismatch': 'Passwords do not match',
  'validation.termsRequired': 'You must accept the terms',
  'validation.pinDigits': 'The code must be numeric',
  'validation.pinMin': 'At least {min} digits',
  'validation.pinMax': 'At most {max} digits',
  'validation.messageTooLong': 'Message too long (500 characters max)',
  'validation.dateRequired': 'Choose a date',
  'validation.dateTooSoon': 'The date must be at least 5 minutes in the future',
  'validation.dateTooFar': 'The date cannot be more than 30 days away',
  'validation.confirmDelete': 'Type DELETE to confirm',

  /* ---- Generic states -------------------------------------------------- */
  'state.errorTitle': 'Something went wrong',
} as const;

/** Every locale must provide exactly these keys. */
export type TranslationKey = keyof typeof en;
export type Dictionary = Record<TranslationKey, string>;
